import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { AnomalyCase } from './types';

const OTHER_REASON_KEY = 'other';

interface Props {
  anomalyCase: AnomalyCase | null;
  knownIssues: Array<{ key: string; label: string }>;
  onClose: () => void;
  onConfirm: (caseId: string, reasonKey: string, note: string) => void;
}

/**
 * Dismissal always carries a reason, picked from a shared list where possible.
 *
 * A free-text-only dismissal loses the thing we most need: a reason that fires over and over
 * is how we find a rule that needs narrowing, rather than a supplier that needs chasing.
 */
export default function DismissCaseDialog({ anomalyCase: c, knownIssues, onClose, onConfirm }: Props) {
  const [knownIssue, setKnownIssue] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (c) {
      setKnownIssue('');
      setNote('');
    }
  }, [c]);

  const confirm = () => {
    if (!c) {
      return;
    }
    onConfirm(c.caseId, knownIssue, note.trim());
  };

  return (
    <Dialog open={!!c} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dismiss {c?.caseId}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {c?.propertyName} · {c?.ratePlanName}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel id="dismiss-reason">Reason</InputLabel>
          <Select
            labelId="dismiss-reason"
            label="Reason"
            value={knownIssue}
            onChange={(e) => setKnownIssue(e.target.value)}
          >
            {knownIssues.map((k) => (
              <MenuItem key={k.key} value={k.key}>
                {k.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Notes (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          Picking a known issue keeps it countable — a reason that keeps firing tells us to narrow the rule rather than
          chase the supplier again.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!knownIssue || (knownIssue === OTHER_REASON_KEY && !note.trim())}
          onClick={confirm}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
}
